import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { ReviewListCard } from "./ReviewListCard";

import { type GetProjectReviewsResponse } from "../../../services/askPunttProjectFeed.types";

/**
 * Right-hand REVIEWS panel: an overall status chip (e.g. IN REVIEW) plus a
 * card per active asset under review (name + version badge + thumbnail).
 * Clicking a card deep-links into the asset's review/ticket via `onOpenReview`.
 */
export function ProjectReviewsPanel({
  data,
  isLoading = false,
  onOpenReview,
  open: openProp,
  onOpenChange,
}: {
  data?: GetProjectReviewsResponse;
  isLoading?: boolean;
  onOpenReview?: (item: GetProjectReviewsResponse["reviews"][number]) => void;
  /** Controlled expand state for the reviews section header. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [openInternal, setOpenInternal] = useState(true);
  const open = openProp ?? openInternal;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (openProp === undefined) setOpenInternal(next);
  };
  const reviews = data?.reviews ?? [];
  const status = data?.status;

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${open ? "h-full flex-1" : "shrink-0"}`}
      aria-label="Reviews"
      data-testid="project-reviews-panel"
      data-panel-expanded={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Toggle reviews section"
        data-testid="project-reviews-toggle"
        className="flex w-full shrink-0 items-center gap-2 bg-base-white px-4 py-3 text-left"
      >
        <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
          Reviews
        </h2>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>

      {open && (
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3 [scrollbar-width:thin]"
          data-testid="project-reviews-scroll"
        >
          {status && (
            <p
              className="text-puntt-yellow-11 mb-3 text-[11px] font-bold uppercase tracking-wide"
              data-testid="project-reviews-status"
            >
              {status}
            </p>
          )}

          {isLoading ? (
            <div
              className="flex items-center justify-center py-4"
              data-testid="project-reviews-loading"
            >
              <CircleNotch
                size={18}
                weight="bold"
                className="animate-spin text-puntt-neutral-gray-9"
              />
            </div>
          ) : reviews.length === 0 ? (
            <p
              className="text-[13px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="project-reviews-empty"
            >
              No active reviews. Reviews appear here automatically when a new
              version is uploaded.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {reviews.map((item) => {
                const hasTicket = Boolean(item.ticketId);
                return (
                  <ReviewListCard
                    key={item.id}
                    data-testid={`project-review-card-${item.id}`}
                    item={{
                      id: item.id,
                      name: item.name,
                      versionNumber: item.versionNumber,
                      thumbnailUrl: item.thumbnailUrl,
                      status: item.status,
                      aiCommentCount: item.aiCommentCount,
                      humanCommentCount: item.humanCommentCount,
                      disabled: !hasTicket,
                      approval: item.approval
                        ? {
                            decision: item.approval.decision,
                            reviewerName: item.approval.reviewerName,
                            at: item.approval.at
                              ? new Date(item.approval.at)
                              : undefined,
                          }
                        : null,
                    }}
                    testIdPrefix="project-review-card"
                    onClick={() => {
                      if (hasTicket) onOpenReview?.(item);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
